import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Award, Clock, ChevronRight } from "lucide-react";
import BecoPanel from "@/components/BecoPanel";

function Gauge({ label, value, testid }) {
  const v = value ?? 0;
  return (
    <div className="k-container" style={{ padding: 18, flex: 1, minWidth: 150 }} data-testid={testid}>
      <div className="k-muted" style={{ fontSize: 12 }}>{label}</div>
      <div className="k-heading" style={{ fontSize: 34, fontWeight: 700 }}>{value == null ? "—" : v}</div>
      <div style={{ height: 6, background: "var(--control)", borderRadius: 999, overflow: "hidden", marginTop: 6 }}>
        <div style={{ width: `${v}%`, height: "100%", background: "var(--accent)" }} />
      </div>
    </div>
  );
}

export default function MindsetTab({ projectId }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/coach/projects/${projectId}/mindset`).then(r => setM(r.data)).catch(() => {}); }, [projectId]);
  if (!m) return <p className="k-muted">Loading…</p>;

  return (
    <div data-testid="mindset-tab">
      {/* GLANCEABLE (Patch 52): the single BeCo Index score + its one-line "guide, not a validated
          measure" label. BecoPanel keeps its own collapsed "Tell me why" drill-down. */}
      <BecoPanel />
      <p className="k-muted" data-testid="mindset-glance-label" style={{ fontSize: 12, margin: "-6px 0 14px" }}>
        A guide to build discipline, not a validated psychological measure.
      </p>

      {/* DETAIL (Patch 52): the Greek-letter breakdown, badges, time-saved and glossary default closed. */}
      <details className="k-container k-collapsible" data-testid="mindset-detail" style={{ padding: 0, marginBottom: 14 }}>
        <summary className="k-collapsible-summary" data-testid="mindset-detail-summary"
          style={{ cursor: "pointer", padding: "16px 18px", display: "flex", alignItems: "center", gap: 10 }}>
          <ChevronRight size={16} className="k-collapsible-chev" aria-hidden="true" />
          <span className="k-heading" style={{ fontSize: 15, fontWeight: 600 }}>Show detailed metrics</span>
          <span className="k-muted" style={{ fontSize: 12, marginLeft: "auto" }}>αB, σB, badges, time saved & glossary</span>
        </summary>
        <div style={{ padding: "0 18px 18px" }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <Gauge label="Momentum αB" value={m.alpha_b} testid="gauge-alpha" />
            <Gauge label="Scope stability σB" value={m.sigma_b} testid="gauge-sigma" />
          </div>

          <div className="k-container" style={{ padding: 18, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Award size={16} color="var(--accent)" /><h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>Badges</h3>
            </div>
            {m.badges.length === 0 ? <p className="k-muted" style={{ fontSize: 13 }}>Earn badges for good practice (never for spending).</p> :
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {m.badges.map(b => <span key={b.id} className="k-badge k-badge-accent" data-testid={`badge-${b.id}`}>{b.label}</span>)}
              </div>}
          </div>

          <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="time-saved">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Clock size={16} color="var(--accent)" />
              <span className="k-heading" style={{ fontSize: 15 }}>~{m.time_saved_estimate.hours}h saved (estimate)</span>
            </div>
            <p className="k-muted" style={{ fontSize: 11, marginTop: 4 }}>{m.time_saved_estimate.method}</p>
          </div>

          <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="glossary">
            <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>Glossary</h3>
            {Object.entries(m.glossary).map(([k, v]) => (
              <div key={k} style={{ marginBottom: 6 }}>
                <span className="k-heading" style={{ fontSize: 13, fontWeight: 700 }}>{k}</span>
                <span className="k-muted" style={{ fontSize: 12 }}> — {v}</span>
              </div>
            ))}
          </div>

          <p className="k-muted" style={{ fontSize: 11 }}>{m.data_rules}</p>
        </div>
      </details>
    </div>
  );
}
